import { AfterViewChecked, ChangeDetectorRef, Directive, ElementRef, inject, input, Renderer2 } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { areStringsEqual } from "@globals/helper-functions";
import { InterpolationParameters } from "@ngx-translate/core";
import { BiTranslateService } from "./bi-translate.service";

@Directive({
  selector: "[translate]"
})
export class BiTranslateDirective implements AfterViewChecked {
  readonly translate = input<string>("");
  readonly translateParams = input<InterpolationParameters>();

  private readonly textNodes = new WeakMap<Node, { original: string; rendered: string }>();

  private readonly translator = inject(BiTranslateService);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly changeDetector = inject(ChangeDetectorRef);

  constructor() {
    this.translator.onTranslationRefresh.pipe(takeUntilDestroyed()).subscribe(() => {
      this.updateTranslations();

      this.changeDetector.markForCheck();
    });
  }

  ngAfterViewChecked() {
    this.updateTranslations();
  }

  /**
   * Keeps the legacy content-as-key syntax, e.g. <span translate>shared.Name</span>, working in existing templates.
   * ngx-translate deprecated this syntax in v18 and plans to remove its built-in support in v19.
   * We handle text-node rendering ourselves, independently of ngx-translate's directive, so those templates can remain unchanged.
   * Also supports explicit input keys and preserves nested elements, whitespace, and original casing for missing translations.
   * @see https://github.com/ngx-translate/core/issues/1618#issue-4253916009
   */
  private updateTranslations() {
    const element = this.element.nativeElement;

    const explicitKey = this.translate();

    if (explicitKey && !element.childNodes.length) this.renderer.appendChild(element, this.renderer.createText(""));

    element.childNodes.forEach(node => {
      if (node.nodeType !== Node.TEXT_NODE) return;

      let content = this.textNodes.get(node);

      if (!content || content.rendered !== node.textContent) {
        content = { original: node.textContent ?? "", rendered: node.textContent ?? "" };

        this.textNodes.set(node, content);
      }

      const key = explicitKey || content.original.trim();

      if (!key) return;

      const value = this.translator.instant(key, this.translateParams());

      const translation = typeof value === "string" ? (areStringsEqual(key, value) ? key : value) : value == null ? key : JSON.stringify(value);

      content.rendered = explicitKey ? translation : content.original.replace(key, translation);

      if (node.textContent !== content.rendered) this.renderer.setValue(node, content.rendered);
    });
  }
}
